'use client';

import { useEffect } from 'react';

/**
 * Adds the agency logo as a favicon when one is uploaded for this workspace.
 * Appended *after* the brand favicon declared in the root layout's metadata so modern
 * browsers (which honour the last `<link rel="icon">` they encounter) pick
 * up the agency branding.
 *
 * We deliberately do NOT remove the brand icon. That `<link>` is owned by
 * React's reconciler; calling `el.remove()` on it detaches the DOM node
 * but leaves the fiber intact, and any subsequent <head> reconciliation
 * (every App Router navigation) tries to update/remove the now-orphan
 * fiber → `Cannot read properties of null (reading 'removeChild')`.
 *
 * The link we append carries a `data-agency-favicon` marker so re-running
 * the effect updates the existing element in place instead of stacking
 * duplicates.
 */
export function AgencyFavicon() {
    useEffect(() => {
        let cancelled = false;
        fetch('/api/public/agency-logo')
            .then(r => r.ok ? r.json() : null)
            .then(json => {
                if (cancelled) return;
                const url: string | null = json?.logo_url ?? null;
                if (!url) return;

                const MARKER = 'data-agency-favicon';
                const existing = document.head.querySelector<HTMLLinkElement>(`link[${MARKER}]`);
                if (existing) {
                    existing.href = url;
                    return;
                }
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = url;
                link.setAttribute(MARKER, 'true');
                document.head.appendChild(link);
            })
            .catch(() => { /* keep brand icon */ });
        return () => { cancelled = true; };
    }, []);

    return null;
}
